import {
  type AcpSessionChangeData,
  type CrowdexAgentReminder,
  type CrowdexSavedAgent,
  type CrowdexUIMessage,
  SETTINGS_CHANGED_PREAMBLE,
} from '@crowdex/agent-contracts';

export type AgentReminderTrigger = CrowdexAgentReminder['trigger'];

const MAX_SYSTEM_REMINDER_LENGTH = 9000;

const CHANGE_LABELS: Record<AcpSessionChangeData['field'], string> = {
  agent: 'Agent',
  model: 'Model',
  effort: 'Reasoning effort',
  permissions: 'Permissions',
  plan: 'Plan first',
  collaboration: 'Collaboration',
  environment: 'Environment',
  repository: 'Repository',
  branch: 'Branch',
};

/** Changes that move the conversation to another agent or runtime. */
const HANDOFF_FIELDS = new Set<AcpSessionChangeData['field']>(['agent', 'environment']);
/** Changes to where the agent works. */
const WORKSPACE_FIELDS = new Set<AcpSessionChangeData['field']>([
  'environment',
  'repository',
  'branch',
]);

/**
 * The settings the user changed since the agent's last turn: the session events recorded between
 * that turn and the message being sent (the last one).
 */
export function settingsChangedSinceLastTurn(
  messages: readonly CrowdexUIMessage[],
): AcpSessionChangeData[] {
  const changes: AcpSessionChangeData[] = [];
  for (let index = messages.length - 2; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined) continue;
    const events = message.parts.filter((part) => part.type === 'data-sessionEvent');
    if (events.length !== message.parts.length) break;
    for (const event of events.reverse()) changes.unshift(...event.data.changes);
  }
  return changes;
}

/** The profile reminders' triggers that the changes fire, besides `before-turn`. */
export function reminderTriggers(
  changes: readonly AcpSessionChangeData[],
): Set<AgentReminderTrigger> {
  const triggers = new Set<AgentReminderTrigger>(['before-turn']);
  if (changes.some((change) => HANDOFF_FIELDS.has(change.field))) triggers.add('after-handoff');
  if (changes.some((change) => WORKSPACE_FIELDS.has(change.field)))
    triggers.add('workspace-changed');
  return triggers;
}

/** Tells the agent which settings the user changed since its last turn: the old ones are over. */
function settingsChangedReminder(changes: readonly AcpSessionChangeData[]): string | undefined {
  if (changes.length === 0) return undefined;
  const lines = changes.map(
    (change) =>
      `- ${CHANGE_LABELS[change.field]}: ${change.from === null ? '' : `${change.from} → `}${change.to}`,
  );
  return [
    '<system_reminder id="crowdex.settings">',
    SETTINGS_CHANGED_PREAMBLE,
    ...lines,
    '</system_reminder>',
  ].join('\n');
}

/** Compile profile-owned reminders for the triggers fired this turn, in priority order. */
export function compileAgentSystemReminder(
  profile: CrowdexSavedAgent | null,
  triggers: ReadonlySet<AgentReminderTrigger> = new Set(['before-turn']),
): string | undefined {
  if (profile === null) return undefined;
  const reminders = (profile.systemReminders ?? [])
    .filter((reminder) => triggers.has(reminder.trigger))
    .sort((left, right) => right.priority - left.priority || left.id.localeCompare(right.id));
  if (reminders.length === 0) return undefined;
  const content = reminders
    .map(
      (reminder) =>
        `<reminder id="${reminder.id}" trigger="${reminder.trigger}">${reminder.content}</reminder>`,
    )
    .join('\n');
  return `<system_reminder agent="${profile.id}">\n${content}\n</system_reminder>`.slice(
    0,
    MAX_SYSTEM_REMINDER_LENGTH,
  );
}

const PERSONA_EXCERPT = 1500;

/** The text of a message, its parts joined. */
function messageWords(message: CrowdexUIMessage): string {
  return message.parts
    .flatMap((part) => (part.type === 'text' ? [part.text] : []))
    .join('')
    .trim();
}

/**
 * `@persona` (design D51): the saved agents the user brought in since the agent's own last reply
 * answered in its place. The agent keeps the conversation: it is told who said what, so that it
 * takes those replies for theirs, not its own.
 */
export function personaExchangeReminder(messages: readonly CrowdexUIMessage[]): string | undefined {
  const lines: string[] = [];
  // The message being sent is the last: what came before it, back to the agent's own reply.
  for (let index = messages.length - 2; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined || message.role !== 'assistant') continue;
    const persona = (message.metadata as { persona?: { name?: unknown } } | undefined)?.persona;
    if (persona === undefined || typeof persona.name !== 'string') break;
    const words = messageWords(message);
    const asked = messages[index - 1];
    lines.unshift(
      `- ${persona.name} answered${asked?.role === 'user' ? ` "${messageWords(asked).slice(0, 300)}"` : ''}: ${words.slice(0, PERSONA_EXCERPT)}`,
    );
  }
  if (lines.length === 0) return undefined;
  return [
    '<system_reminder id="crowdex.personas">',
    'The user brought other agents into this conversation for one reply each. These replies are theirs, not yours; you keep the conversation and may build on them.',
    ...lines,
    '</system_reminder>',
  ].join('\n');
}

export function mergeSystemReminders(
  ...reminders: Array<string | null | undefined>
): string | undefined {
  const content = reminders.filter((value): value is string => Boolean(value?.trim())).join('\n\n');
  return content.length === 0 ? undefined : content.slice(0, 10_000);
}

/**
 * The context the core adds to a turn, by source: the core's own (its browser, the settings
 * changed since the last turn), the agent profile's reminders for the triggers the changes fire,
 * and the plugin the user works with.
 */
export function turnSystemReminder(input: {
  messages: readonly CrowdexUIMessage[];
  core: readonly (string | undefined)[];
  profile: CrowdexSavedAgent | null;
  plugin: string | undefined;
}): string | undefined {
  const changes = settingsChangedSinceLastTurn(input.messages);
  return mergeSystemReminders(
    ...input.core,
    settingsChangedReminder(changes),
    personaExchangeReminder(input.messages),
    compileAgentSystemReminder(input.profile, reminderTriggers(changes)),
    input.plugin,
  );
}
